import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';
import type { Site } from '@/types/models';

export interface SiteFormData {
  name: string;
  description?: string;
  address?: string;
  suite?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

// Query hooks
export function useSites(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['sites', organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');

      const { data, error } = await supabase
        .from('sites_with_location_count')
        .select('*')
        .eq('organization_id', organizationId)
        .order('name', { ascending: true });

      if (error) {
        // Fallback to sites table if view doesn't exist
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('sites')
          .select('*')
          .eq('organization_id', organizationId)
          .order('name', { ascending: true });

        if (fallbackError) throw fallbackError;
        return fallbackData as Site[];
      }
      return data as Site[];
    },
    enabled: !!organizationId,
  });
}

export function useSite(siteId: string | undefined) {
  return useQuery({
    queryKey: ['sites', siteId],
    queryFn: async () => {
      if (!siteId) throw new Error('Site ID is required');

      const { data, error } = await supabase
        .from('sites')
        .select('*')
        .eq('id', siteId)
        .single();

      if (error) throw error;
      return data as Site;
    },
    enabled: !!siteId,
  });
}

// Mutation hooks
export function useCreateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SiteFormData & { organization_id: string }) => {
      const { error, data: newSite } = await supabase
        .from('sites')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return newSite as Site;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      notifications.show({
        title: 'Success',
        message: 'Primary location created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create primary location',
        color: 'red',
      });
    },
  });
}

export function useUpdateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<SiteFormData> }) => {
      const { error, data: updatedSite } = await supabase
        .from('sites')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return updatedSite as Site;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      notifications.show({
        title: 'Success',
        message: 'Primary location updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update primary location',
        color: 'red',
      });
    },
  });
}

export function useDeleteSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (siteId: string) => {
      const { error } = await supabase.from('sites').delete().eq('id', siteId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] }); // Locations may be affected
      notifications.show({
        title: 'Success',
        message: 'Primary location deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete primary location',
        color: 'red',
      });
    },
  });
}

// Get formatted address for a site
export function formatSiteAddress(site: Site): string {
  const parts: string[] = [];

  if (site.address) parts.push(site.address);
  if (site.suite) parts.push(`Suite ${site.suite}`);

  const cityStateZip: string[] = [];
  if (site.city) cityStateZip.push(site.city);
  if (site.state) cityStateZip.push(site.state);
  if (site.postal_code) cityStateZip.push(site.postal_code);

  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(', '));
  }

  if (site.country && site.country !== 'USA' && site.country !== 'United States') {
    parts.push(site.country);
  }

  return parts.join(', ');
}
